import {
  Button,
  Card,
  FormInstance,
  message,
  Table,
  TableColumnsType
} from 'antd'
import { TableProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  GetPersonListItemByDataResponse,
  GetPotentialContactResponse
} from 'src/api/requests'
import { useStyles } from 'src/features/persons/styles'
import { GenderShortMap } from 'src/features/persons/utils'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

import { ContactsAddForm } from './ContactsAddForm'

type Props = {
  isLoading?: boolean
  data?: GetPersonListItemByDataResponse[]
  onCancel?: () => void
  form?: FormInstance
} & TableProps

const DEFAULT_USER_LIST_PER_PAGE = 5

export const SearchPersonTable = ({
  data,
  isLoading,
  onCancel,
  form,
  ...props
}: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const [selectedPage, setSelectedPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_USER_LIST_PER_PAGE)
  const [isOpen, setIsOpen] = useState(false)
  const [personId, setPersonId] = useState('')

  const onToggle = () => setIsOpen((prev) => !prev)

  const paginatedSelectedUsers = data?.slice(
    (selectedPage - 1) * pageSize,
    selectedPage * pageSize
  )

  const { id, personId: currentPersonId } = useParams()

  const onAddContacts = (personId: string) => {
    if (currentPersonId === personId)
      return message.error(t('consultations.contacts_add_self_error'))

    if (form) {
      const contactsList = form.getFieldValue('contactsList')

      if (
        contactsList?.some(
          (contact: GetPotentialContactResponse) =>
            contact.personId === personId
        )
      )
        return message.error(t('consultations.contacts_exists'))
    }

    setPersonId(personId)
    onToggle()
  }

  const columns = useMemo(
    (): TableColumnsType<GetPersonListItemByDataResponse> => [
      {
        key: 'lastName',
        title: t('form.last_name'),
        dataIndex: 'lastName'
      },
      {
        key: 'firstName',
        title: t('form.first_name'),
        dataIndex: 'firstName'
      },
      {
        key: 'idnp',
        title: t('form.idnp'),
        dataIndex: 'idnp'
      },
      {
        key: 'birth_date',
        title: t('form.birth_date'),
        render: (_, { birthDate }) =>
          dayjs(birthDate).format(dayMonthYearFormatWithDot),
        align: 'center'
      },
      {
        key: 'gender',
        title: t('form.gender'),
        render: (_, { gender }) => (gender ? GenderShortMap[gender] : '-'),
        align: 'center',
        width: '84px'
      },
      {
        key: 'institution',
        title: t('common.institution'),
        render: (_, { institution }) => institution?.name ?? '-',
        align: 'center'
      },
      {
        key: 'doctor',
        title: t('common.doctor'),
        render: (_, { doctor }) =>
          doctor ? `${doctor?.firstName} ${doctor?.lastName}` : '-',
        align: 'center'
      },
      {
        key: 'actions',
        title: t('common.action'),
        align: 'center',
        render: (person) => {
          const onClick = () => {
            onAddContacts(person?.id)
          }

          return (
            <Button type="link" onClick={onClick}>
              {t('common.add')}
            </Button>
          )
        }
      }
    ],
    [t]
  )

  return (
    <>
      <Card className={styles.personsCard}>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={paginatedSelectedUsers}
          columns={columns}
          pagination={{
            current: selectedPage,
            pageSize,
            total: data?.length,
            showSizeChanger: true,
            onShowSizeChange: (_, size) => setPageSize(size),
            showTotal: (total) => `${t('common.total')} ${total}`,
            onChange: (page) => setSelectedPage(page),
            hideOnSinglePage: true
          }}
          scroll={{ x: 'max-content' }}
          {...props}
        />
      </Card>

      {isOpen && (
        <ContactsAddForm
          onModalCancel={onToggle}
          onCancel={onCancel}
          form={form}
          consultationId={String(id)}
          personId={personId}
        />
      )}
    </>
  )
}
